import {
  Box,
  Button,
  Card,
  Container,
  Embed,
  Flex,
  Icon,
  Modal,
  Separator,
} from '@energiebespaarders/symbols';
import { Book, Center, Large, Smaller } from '@energiebespaarders/symbols/helpers';
import { Play, ThumbsUp } from '@energiebespaarders/symbols/icons/line';
import mediaMin from '@energiebespaarders/symbols/styles/breakpoints';
import { padding } from '@energiebespaarders/symbols/styles/mixins';
import Head from 'next/head';
import { rgba } from 'polished';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import HybridLink from '~/components/HybridLink';
import type { TestimonialObj } from '~/components/Testimonials/testimonialsContent';
import testimonials from '~/components/Testimonials/testimonialsContent';
import theme from '../../styles/theme';
import type { GetStaticPaths, GetStaticProps } from 'next';

const StyledTestimonialHero = styled(Box)`
  position: relative;
  min-height: 300px;
  background-size: cover;
  color: white;

  ${mediaMin.md} {
    min-height: 600px;
  }
`;

const StyledQuote = styled.h1`
  ${padding(5, 'b')};
  position: relative;
  margin-top: -100px;
  font-family: ${x => x.theme.type.fontFamilySerif};
  font-style: italic;
  line-height: 1.5;
  text-align: center;
`;

const StyledReasonBubble = styled(Card)`
  font-family: ${x => x.theme.type.fontFamilySerif};
  font-style: italic;

  &::after {
    position: absolute;
    display: block;
    width: 0;
    height: 0;
    border-style: solid;
    content: '';
    transform: scale(0.9999);
    bottom: -10px;
    border-width: 10px 10px 0 10px;
    border-color: white transparent transparent transparent;
    right: 10%;
  }
`;

const StyledHeadshot = styled(Box)`
  position: relative;
  margin-right: 0;
  margin-left: auto;
  width: 125px;
  height: 125px;
  border: 3px solid white;
  border-radius: 100px;
  background-size: cover;
  box-shadow: 0 0 12px -6px ${x => x.theme.colors.grayDarker};
  cursor: pointer;
  transition: all 0.25s;
`;

const StyledPullQuote = styled.aside`
  position: relative;
  float: right;
  color: ${x => x.theme.colors.purple};
  text-align: center;
  font-weight: 500;
  font-family: ${x => x.theme.type.fontFamilySerif};
  font-style: italic;
  font-size: ${x => x.theme.type.scale[4]};

  ${mediaMin.md} {
    max-width: 50%;
  }
`;

const StyledPhoto = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  border-radius: 3px;
  background-position: center;
  background-size: cover;
`;

const StyledPlayCircle = styled.div`
  top: -22.5px;
  left: -30px;
  width: 75px;
  height: 75px;
  border: 3px solid rgba(255, 255, 255, 0.9);
  border-radius: 1000px;
  background: ${x => rgba(x.theme.colors.green, 0.5)};
  transition: all 0.25s;

  ${mediaMin.md} {
    position: absolute;
    top: -45px;
    left: -62.5px;
    width: 150px;
    height: 150px;
  }
`;

const StyledPlayTriangle = styled.div`
  position: relative;
  top: -52px;
  left: 30px;
  width: 30px;
  height: 30px;
  border-top-right-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  text-align: left;
  transition: all 0.3s;
  transform: rotate(30deg) skewX(-30deg) scale(1, 0.866);

  ${mediaMin.md} {
    top: 0;
    left: 0;
    width: 60px;
    height: 60px;
  }

  &::before,
  &::after {
    position: absolute;
    width: 30px;
    height: 30px;
    border-top-right-radius: 6px;
    background: rgba(255, 255, 255, 0.75);
    content: '';

    ${mediaMin.md} {
      width: 60px;
      height: 60px;
    }
  }

  &::before {
    transition: all 0.15s;
    transform: rotate(-135deg) skewX(-45deg) scale(1.414, 0.707) translate(0, -50%);
  }

  &::after {
    transition: all 0.45s;
    transform: rotate(135deg) skewY(-45deg) scale(0.707, 1.414) translate(50%);
  }
`;

const StyledPlayButton = styled.div`
  position: absolute;
  top: calc(50% - 37.5px);
  left: calc(50% - 37.5px);
  cursor: pointer;

  ${mediaMin.md} {
    top: calc(50% - 30px);
    left: calc(50% - 30px);
  }

  &:hover {
    ${StyledPlayCircle} {
      border: 3px solid ${x => x.theme.colors.green};
      background: ${x => rgba(x.theme.colors.grayDarker, 0.25)};
    }

    ${StyledPlayTriangle} {
      background: ${x => rgba(x.theme.colors.green, 0.75)};

      &::before,
      &::after {
        background: ${x => rgba(x.theme.colors.green, 0.75)};
      }
    }
  }
`;

const Testimonial: React.FC<{ testimonial: TestimonialObj }> = ({ testimonial }) => {
  const [videoOpen, setVideoOpen] = useState(false);

  const openVideoModal = useCallback(() => {
    setVideoOpen(true);
  }, []);

  const closeVideoModal = useCallback(() => {
    setVideoOpen(false);
  }, []);

  const PlayTriangle = () => (
    <StyledPlayButton onClick={openVideoModal}>
      <StyledPlayCircle />
      <StyledPlayTriangle />
    </StyledPlayButton>
  );

  const renderPullQuote = (paragraphIndex: number) => {
    if (testimonial && testimonial.pullQuotes.length > 0) {
      const pullQuote = testimonial.pullQuotes[paragraphIndex];
      if (pullQuote) {
        return (
          <StyledPullQuote>
            <blockquote>"{pullQuote}"</blockquote>
          </StyledPullQuote>
        );
      }
    }
  };

  /** One photo every two paragraphs */
  const renderBodyPhoto = (paragraphIndex: number) => {
    if (testimonial && testimonial.bodyPhotos.length > 0) {
      const photoIndex = Math.floor(paragraphIndex / 2);
      if (paragraphIndex % 2 === 1) {
        const photo = testimonial.bodyPhotos[photoIndex];
        return photo ? (
          <Box style={{ clear: 'both' }} my={3}>
            <StyledPhoto
              style={{
                backgroundImage: `url(${photo})`,
              }}
            />
          </Box>
        ) : (
          <></>
        );
      }
    }
  };

  return (
    <>
      <Head>
        <title key="title">{testimonial.customerName} • Energiebespaarders</title>
        <meta
          name="description"
          key="meta-description"
          content="Klanten vertellen over hun ervaringen bij Energiebespaarders"
        />
        <link
          rel="canonical"
          href={`https://energiebespaarders.nl/klanten/${testimonial.key}`}
          key="meta-canonical"
        />
      </Head>
      <StyledTestimonialHero
        py={10}
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.25), rgba(255,255,255,1)), url(${testimonial.bgPhoto})`,
          backgroundPosition: `${
            testimonial.backgroundPosition ? testimonial.backgroundPosition : 'top 25%'
          } center`,
        }}
      >
        {testimonial.videoId && <PlayTriangle />}
      </StyledTestimonialHero>
      <Container>
        <Box width={1} pb={5}>
          <StyledQuote>"{testimonial.mainQuote}"</StyledQuote>
          <div className="deb-divider" />
          <Center block>
            <Large>
              {testimonial.customerName} uit {testimonial.city}, <br />
              Energiebespaarders sinds {testimonial.date}
            </Large>
            {testimonial.videoId && (
              <div>
                <Button minimal onClick={openVideoModal} iconStart={Play}>
                  Bekijk de video
                </Button>
              </div>
            )}
          </Center>
        </Box>
      </Container>
      <Box width={1} py={10} style={{ background: theme.colors.grayLighter }}>
        <Container>
          <Flex flexWrap="wrap" mx={-3}>
            <Box width={[1, 1 / 2]} px={3}>
              <Large>Bespaart energie met</Large>
              <Separator my={2} color="grayLight" />
              <ul className="checks">
                {testimonial.productTypes.map((p, index) => (
                  <li key={index}>
                    <div>
                      <Book>{p}</Book>
                    </div>
                    <Smaller>{testimonial.productNames[index]}</Smaller>
                  </li>
                ))}
              </ul>
            </Box>
            <Box width={[1, 1 / 2]} px={3}>
              <StyledReasonBubble p={3} animation="none">
                "{testimonial.mainReason}"
              </StyledReasonBubble>
              <StyledHeadshot
                style={{
                  backgroundImage: `url(${testimonial.profilePhoto})`,
                }}
                onClick={openVideoModal}
                mt={2}
              />
              <Modal
                isOpen={videoOpen && !!testimonial.videoId}
                onRequestClose={closeVideoModal}
                mobile={false}
                contentPadding={0}
              >
                <Embed
                  src={`https://www.youtube-nocookie.com/embed/${testimonial.videoId}?rel=0&amp;showinfo=0&amp;autoplay=1`}
                  allowFullScreen
                  title={`Bekijk het verhaal van ${testimonial.customerName}`}
                />
              </Modal>
            </Box>
          </Flex>
        </Container>
      </Box>

      <Box width={1} py={10}>
        <Container size="md">
          {testimonial.paragraphs.map((paragraph, index) => (
            <React.Fragment key={`paragraph-${index}`}>
              <p>{paragraph}</p>
              {renderBodyPhoto(index)}
              {renderPullQuote(index)}
            </React.Fragment>
          ))}
          {testimonial.videoId && (
            <div className="link-green" onClick={openVideoModal}>
              <Icon icon={Play} mr={1} stroke="green" /> Bekijk de video
            </div>
          )}
          <Separator my={2} color="grayLight" />
          <HybridLink to="/klanten">
            <Icon icon={ThumbsUp} mr={1} stroke="green" /> Lees meer verhalen van trotse
            Energiebespaarders
          </HybridLink>
        </Container>
      </Box>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: testimonials.map(t => ({ params: { name: t.key } })),
    fallback: false, // will result in 404 for paths that don't match
  };
};

// This also gets called at build time
export const getStaticProps: GetStaticProps = ({ params }) => {
  const testimonial = testimonials.find(t => t.key === params?.name);

  if (!testimonial) {
    // not needed with getStaticPaths, but just to be sure
    return {
      notFound: true,
    };
  }

  // Pass post data to the page via props
  return { props: { testimonial } };
};

export default Testimonial;
